/**
 * The one way site code talks to Google Tag Manager: pushes into
 * window.dataLayer. GTM (public/index.html) loads only on torsunov.com and
 * never inside an iframe; everywhere else the array is still there and the
 * pushes are harmless.
 *
 * Every push goes through the guard below — GA4 silently drops events that
 * break its limits, so they are enforced here: event and param names are
 * snake_case ≤ 40 chars, undefined/null/objects are dropped, strings are cut
 * to 100 chars (page_location / page_referrer / page_title get GA4's own
 * longer limits, otherwise gclid in page_location would be cut), at most 25
 * params. Internal traffic (internal.ts) gets `traffic_type: "internal"`.
 *
 * Every push also carries the REDACTED page_location / page_referrer
 * (redact.ts) unless the caller set them: GTM's GA4 event settings map them,
 * so gtag never falls back to the raw address with ?supq=, ?session_id= ….
 * After a sign-in / payment hop (Google, Apple, Stripe) page_referrer is our
 * own origin, never the hop (referrer.ts).
 */
import { isInternalTraffic } from "./internal";
import { redactUrl } from "./redact";
import { analyticsReferrer } from "./referrer";

export type ParamValue = string | number | boolean;
export type EventParams = Record<string, unknown>;

export const MAX_PARAMS = 25;
export const MAX_STRING = 100;
export const MAX_NAME = 40;

// GA4's own limits for these params are longer than 100.
const LONG_PARAMS: Record<string, number> = {
    page_location: 1000,
    page_referrer: 420,
    page_title: 300,
};

const NAME_RE = /^[a-z][a-z0-9_]*$/;
const RESERVED_PREFIX_RE = /^(?:ga_|google_|firebase_|gtm)/;

/** snake_case name ≤ 40 chars, or null when nothing usable is left. */
export const normalizeName = (raw: string): string | null => {
    const s = String(raw ?? "")
        .trim()
        .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, MAX_NAME)
        .replace(/_+$/, "");
    if (!s || !NAME_RE.test(s) || RESERVED_PREFIX_RE.test(s)) return null;
    return s;
};

const cut = (s: string, max: number): string => (s.length > max ? s.slice(0, max) : s);

/** Flat GA4-safe params (see the header comment). */
export const guardParams = (params: EventParams | undefined, limit = MAX_PARAMS): Record<string, ParamValue> => {
    const out: Record<string, ParamValue> = {};
    if (!params) return out;
    let n = 0;
    for (const [rawKey, v] of Object.entries(params)) {
        if (n >= limit) break;
        if (v === undefined || v === null) continue;
        const key = normalizeName(rawKey);
        if (!key || key === "event" || key in out) continue;
        let value: ParamValue;
        if (typeof v === "string") value = cut(v, LONG_PARAMS[key] ?? MAX_STRING);
        else if (typeof v === "number") {
            if (!Number.isFinite(v)) continue;
            value = v;
        } else if (typeof v === "boolean") value = v;
        else continue;
        out[key] = value;
        n++;
    }
    return out;
};

type DataLayerWindow = Window & { dataLayer?: unknown[] };

/** page_location / page_referrer of the current address, redacted (hops: referrer.ts). */
const pageAddress = (): { page_location?: string; page_referrer?: string } => {
    try {
        return {
            page_location: redactUrl(window.location.href) || undefined,
            page_referrer: analyticsReferrer(document.referrer) || undefined,
        };
    } catch {
        return {};
    }
};

export const dataLayer = (): unknown[] => {
    const w = window as DataLayerWindow;
    if (!Array.isArray(w.dataLayer)) w.dataLayer = [];
    return w.dataLayer;
};

/** GTM's own delivery hook: called once the tags of that event have fired. */
export interface EventCallbackHook {
    eventCallback: () => void;
    eventTimeout: number;
}

let armedHook: EventCallbackHook | null = null;

/**
 * The next push that carries an `event` gets `hook` (navigate.ts). Returns a
 * disarm function that reports whether the hook was still unused.
 */
export const armNextEventPush = (hook: EventCallbackHook): (() => boolean) => {
    armedHook = hook;
    return () => {
        const unused = armedHook === hook;
        if (unused) armedHook = null;
        return unused;
    };
};

/** Raw push — only for objects already shaped by track / trackEcommerce. */
export const pushRaw = (obj: Record<string, unknown>): void => {
    try {
        if (armedHook && typeof obj.event === "string") {
            obj = { ...obj, ...armedHook };
            armedHook = null;
        }
        dataLayer().push(obj);
    } catch {
        // analytics must never break the page
    }
};

/**
 * A GA4 event through GTM. Returns false when the event name is unusable and
 * nothing was pushed.
 */
export const track = (event: string, params?: EventParams): boolean => {
    const name = normalizeName(event);
    if (!name) return false;
    const internal = isInternalTraffic();
    // The address first, so it survives the 25-param cut; the caller's own wins.
    const guarded = guardParams({ ...pageAddress(), ...params }, internal ? MAX_PARAMS - 1 : MAX_PARAMS);
    delete guarded.traffic_type;
    pushRaw({ event: name, ...guarded, ...(internal ? { traffic_type: "internal" } : {}) });
    return true;
};

export interface EcommerceItem {
    item_id: string;
    item_name: string;
    item_category?: string;
    price: number;
    quantity: number;
    [extra: string]: unknown;
}

export interface EcommercePayload {
    currency: "USD" | string;
    value: number;
    items: EcommerceItem[];
    transaction_id?: string;
    coupon?: string;
}

const guardItem = (item: EcommerceItem): Record<string, ParamValue> => {
    // Items take up to 27 params in GA4; ours carry a handful.
    const g = guardParams(item, 27);
    return {
        ...g,
        item_id: cut(String(item.item_id ?? ""), MAX_STRING),
        item_name: cut(String(item.item_name ?? item.item_id ?? ""), MAX_STRING),
        price: Number.isFinite(item.price) ? item.price : 0,
        quantity: Number.isFinite(item.quantity) && item.quantity > 0 ? item.quantity : 1,
    };
};

const PURCHASE_LATCH_KEY = "analytics_purchases_sent";

/** One web `purchase` per transaction_id on this device (reloads, Back). */
const purchaseAlreadySent = (txId: string): boolean => {
    try {
        const raw = localStorage.getItem(PURCHASE_LATCH_KEY);
        const ids: string[] = raw ? JSON.parse(raw) : [];
        if (Array.isArray(ids) && ids.includes(txId)) return true;
        const next = [...(Array.isArray(ids) ? ids : []), txId].slice(-30);
        localStorage.setItem(PURCHASE_LATCH_KEY, JSON.stringify(next));
    } catch {
        // no storage — at worst a duplicate GA4 dedups by transaction_id
    }
    return false;
};

/**
 * A GA4 ecommerce event (view_item, begin_checkout, purchase, …). The
 * previous ecommerce object is cleared first, as GTM requires. `extra` are
 * flat event params next to `ecommerce` (kind, subscription_event, …).
 * A `purchase` with a transaction_id already pushed on this device is skipped
 * (returns false); the server sends the same id via Measurement Protocol and
 * GA4 dedups the pair.
 */
export const trackEcommerce = (event: string, payload: EcommercePayload, extra?: EventParams): boolean => {
    const name = normalizeName(event);
    if (!name) return false;
    const txId = payload.transaction_id ? cut(String(payload.transaction_id), MAX_STRING) : undefined;
    if (name === "purchase" && txId && purchaseAlreadySent(txId)) return false;
    const ecommerce: Record<string, unknown> = {
        currency: (payload.currency || "USD").toUpperCase(),
        value: Number.isFinite(payload.value) ? Math.round(payload.value * 100) / 100 : 0,
        items: (payload.items || []).slice(0, 200).map(guardItem),
    };
    if (txId) ecommerce.transaction_id = txId;
    if (payload.coupon) ecommerce.coupon = cut(String(payload.coupon), MAX_STRING);
    const internal = isInternalTraffic();
    const flat = guardParams({ ...pageAddress(), ...extra }, internal ? MAX_PARAMS - 1 : MAX_PARAMS);
    delete flat.ecommerce;
    delete flat.traffic_type;
    pushRaw({ ecommerce: null });
    pushRaw({ event: name, ...flat, ecommerce, ...(internal ? { traffic_type: "internal" } : {}) });
    return true;
};
